import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import { AcessoPerfil, AcessoPessoa, AcessoSituacao } from "../../../../interfaces/acessos";
import {
  definirPerfilAcesso,
  desativarPessoa,
  getPerfisAcesso,
  getPessoasAcesso,
  reativarPessoa,
  reenviarConvite,
} from "../../../../services/acessosApi";
import {
  BTN_PRIMARIO,
  BTN_SECUNDARIO,
  Carregando,
  Erro,
  INPUT,
  LABEL,
  ROW,
  TD,
  TH,
  Vazio,
  WRAP_TABELA,
  formatarDataHora,
  msgErro,
  normalizar,
} from "../../paineis-admin/components/shared";
import ConfirmarDesativarModal from "./ConfirmarDesativarModal";
import ConvidarModal from "./ConvidarModal";
import { SeloSituacao, rotuloSituacao } from "./situacao";

const SITUACOES: AcessoSituacao[] = ["Ativo", "ConvitePendente", "ConviteExpirado", "Desativado"];

const ACAO =
  "rounded-lg px-2.5 py-1 text-xs font-semibold text-dv-blue-600 transition-all hover:bg-dv-blue-500/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-dv-blue-500/40 disabled:cursor-not-allowed disabled:opacity-50 dark:text-dv-cyan-300";
const ACAO_PERIGO =
  "rounded-lg px-2.5 py-1 text-xs font-semibold text-red-600 transition-all hover:bg-red-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500/40 disabled:cursor-not-allowed disabled:opacity-50 dark:text-red-300 dark:hover:bg-red-900/20";

const PessoasTab: React.FC<{ orgId: number }> = ({ orgId }) => {
  const [pessoas, setPessoas] = useState<AcessoPessoa[]>([]);
  const [perfis, setPerfis] = useState<AcessoPerfil[]>([]);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState(false);
  const [busca, setBusca] = useState("");
  const [filtro, setFiltro] = useState<AcessoSituacao | "">("");

  const [convidando, setConvidando] = useState(false);
  const [editando, setEditando] = useState<{ pessoaId: number; perfilId: string } | null>(null);
  const [desativar, setDesativar] = useState<AcessoPessoa | null>(null);
  /** Pessoa com uma chamada em andamento: trava os botões da linha contra clique duplo. */
  const [ocupada, setOcupada] = useState<number | null>(null);

  const carregar = useCallback(async () => {
    try {
      const [lista, atribuiveis] = await Promise.all([getPessoasAcesso(orgId), getPerfisAcesso(orgId)]);
      setPessoas(lista);
      setPerfis(atribuiveis);
      setErro(false);
    } catch {
      setErro(true);
    } finally {
      setLoading(false);
    }
  }, [orgId]);

  useEffect(() => {
    carregar();
  }, [carregar]);

  const visiveis = useMemo(() => {
    const termo = normalizar(busca);
    return pessoas.filter(
      (p) =>
        (!filtro || p.situacao === filtro) &&
        (!termo || normalizar(p.nome).includes(termo) || normalizar(p.email).includes(termo))
    );
  }, [pessoas, busca, filtro]);

  /** Roda uma ação da linha: trava, avisa o resultado e recarrega a lista. */
  const executar = async (pessoa: AcessoPessoa, acao: () => Promise<void>, sucesso: string): Promise<boolean> => {
    setOcupada(pessoa.pessoaId);
    try {
      await acao();
      toast.success(sucesso);
      await carregar();
      return true;
    } catch (e) {
      toast.error(msgErro(e, "Não foi possível concluir a ação. Tente de novo."));
      return false;
    } finally {
      setOcupada(null);
    }
  };

  const salvarPerfil = async (pessoa: AcessoPessoa) => {
    if (!editando || editando.perfilId === "") return;
    const ok = await executar(
      pessoa,
      () => definirPerfilAcesso(orgId, pessoa.pessoaId, Number(editando.perfilId)),
      `Perfil de ${pessoa.nome} atualizado.`
    );
    if (ok) setEditando(null);
  };

  const confirmarDesativar = async () => {
    if (!desativar) return;
    const ok = await executar(desativar, () => desativarPessoa(orgId, desativar.pessoaId), `${desativar.nome} foi desativada.`);
    if (ok) setDesativar(null);
  };

  if (loading) return <Carregando rotulo="Carregando pessoas" />;
  if (erro)
    return (
      <Erro
        texto="Não foi possível carregar as pessoas. Tente de novo."
        onTentar={() => {
          setLoading(true);
          carregar();
        }}
      />
    );

  return (
    <section aria-label="Pessoas da organização" className="space-y-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
        <div className="flex-1">
          <label htmlFor="acessos-busca" className={LABEL}>
            Buscar
          </label>
          <input
            id="acessos-busca"
            type="search"
            className={`${INPUT} mt-1`}
            placeholder="Nome ou e-mail"
            value={busca}
            onChange={(e) => setBusca(e.target.value)}
          />
        </div>
        <div className="sm:w-56">
          <label htmlFor="acessos-situacao" className={LABEL}>
            Situação
          </label>
          <select
            id="acessos-situacao"
            className={`${INPUT} mt-1`}
            value={filtro}
            onChange={(e) => setFiltro(e.target.value as AcessoSituacao | "")}
          >
            <option value="">Todas</option>
            {SITUACOES.map((s) => (
              <option key={s} value={s}>
                {rotuloSituacao(s)}
              </option>
            ))}
          </select>
        </div>
        <button type="button" className={BTN_PRIMARIO} onClick={() => setConvidando(true)}>
          Convidar pessoa
        </button>
      </div>

      {pessoas.length === 0 ? (
        <Vazio texto="Ninguém tem acesso a esta organização ainda. Use Convidar pessoa para liberar o primeiro acesso." />
      ) : visiveis.length === 0 ? (
        <Vazio texto="Nenhuma pessoa corresponde à busca ou ao filtro." />
      ) : (
        <div className={WRAP_TABELA}>
          <table className="w-full min-w-[860px]">
            <thead>
              <tr className="border-b border-gray-100 dark:border-white/10">
                <th className={TH}>Nome</th>
                <th className={TH}>E-mail</th>
                <th className={TH}>Perfil</th>
                <th className={TH}>Situação</th>
                <th className={TH}>Último acesso</th>
                <th className={TH}>Ações</th>
              </tr>
            </thead>
            <tbody>
              {visiveis.map((p) => {
                const editandoEsta = editando?.pessoaId === p.pessoaId;
                const travada = ocupada === p.pessoaId;
                const convite = p.situacao === "ConvitePendente" || p.situacao === "ConviteExpirado";
                return (
                  <tr key={p.pessoaId} className={ROW}>
                    <td className={`${TD} font-semibold text-navy-700 dark:text-white`}>{p.nome}</td>
                    <td className={TD}>{p.email}</td>
                    <td className={TD}>
                      {editandoEsta ? (
                        <div className="flex items-center gap-2">
                          <select
                            aria-label={`Perfil de ${p.nome}`}
                            className={INPUT}
                            value={editando.perfilId}
                            onChange={(e) => setEditando({ pessoaId: p.pessoaId, perfilId: e.target.value })}
                          >
                            <option value="">Escolha um perfil</option>
                            {perfis.map((pf) => (
                              <option key={pf.id} value={pf.id}>
                                {pf.nome}
                              </option>
                            ))}
                          </select>
                          <button
                            type="button"
                            className={BTN_PRIMARIO}
                            disabled={travada || editando.perfilId === ""}
                            onClick={() => salvarPerfil(p)}
                          >
                            Salvar
                          </button>
                          <button type="button" className={BTN_SECUNDARIO} onClick={() => setEditando(null)}>
                            Cancelar
                          </button>
                        </div>
                      ) : (
                        p.perfilNome || "-"
                      )}
                    </td>
                    <td className={TD}>
                      <SeloSituacao situacao={p.situacao} />
                    </td>
                    <td className={`${TD} whitespace-nowrap`}>{formatarDataHora(p.ultimoAcesso)}</td>
                    <td className={`${TD} whitespace-nowrap`}>
                      {p.situacao === "Desativado" ? (
                        <button
                          type="button"
                          className={ACAO}
                          disabled={travada}
                          onClick={() =>
                            executar(p, () => reativarPessoa(orgId, p.pessoaId), `${p.nome} foi reativada.`)
                          }
                        >
                          Reativar
                        </button>
                      ) : (
                        <>
                          <button
                            type="button"
                            className={ACAO}
                            disabled={travada}
                            onClick={() =>
                              setEditando({ pessoaId: p.pessoaId, perfilId: p.perfilId ? String(p.perfilId) : "" })
                            }
                          >
                            Trocar perfil
                          </button>
                          {convite && (
                            <button
                              type="button"
                              className={ACAO}
                              disabled={travada}
                              onClick={() =>
                                executar(p, () => reenviarConvite(orgId, p.pessoaId), `Convite reenviado para ${p.email}.`)
                              }
                            >
                              Reenviar convite
                            </button>
                          )}
                          <button type="button" className={ACAO_PERIGO} disabled={travada} onClick={() => setDesativar(p)}>
                            Desativar
                          </button>
                        </>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {convidando && (
        <ConvidarModal orgId={orgId} perfis={perfis} onClose={() => setConvidando(false)} onConvidado={carregar} />
      )}
      {desativar && (
        <ConfirmarDesativarModal
          pessoa={desativar}
          desativando={ocupada === desativar.pessoaId}
          onConfirmar={confirmarDesativar}
          onClose={() => setDesativar(null)}
        />
      )}
    </section>
  );
};

export default PessoasTab;
